/**
 * A pooled cache of pre-computed cell runs for the hand-made FX shapes (filled discs, crescents, rings). The per-cell
 * loops (hypot / inside tests / colour picks) run once per (shape, size, tone); every later frame replays the shape as
 * a few horizontal `fillRect` runs. Runs, not pictures: the FX pixel pen (render/fx/pixelRules.ts) rasterises rects on
 * its cell grid but treats a `drawImage` as a picture to convert (flush + getImageData), which costs more than it saves.
 * Each run carries a dither level (0..3, as kit.level) that is capped by the level asked at draw time. Bounded: past CAP
 * entries the oldest is dropped. Used by arts/moon.ts; any art may use it.
 */
import { DITHER, type G } from './kit';

/** Runs of one shape, flat: [x, y, w, level, colour index] per run (cell coordinates from the shape's origin). */
export interface Runs {
  n: number;
  d: Int16Array;
  css: string[];
}

export type Put = (x: number, y: number, css: string, lvl?: number) => void;

const CAP = 400;
const cache = new Map<string, Runs>();

/** The runs for `key`; `paint` calls `put(x, y, css, lvl = 3)` for each cell once (cells relative to the origin). */
export function runsOf(key: string, paint: (put: Put) => void): Runs {
  const hit = cache.get(key);
  if (hit) return hit;
  const cells = new Map<number, [string, number]>();
  paint((x, y, css, lvl = 3) => {
    cells.set((Math.round(y) + 4096) * 8192 + Math.round(x) + 4096, [css, lvl]);
  });
  const keys = [...cells.keys()].sort((a, b) => a - b);
  const css: string[] = [];
  const out: number[] = [];
  let i = 0;
  while (i < keys.length) {
    const k0 = keys[i]!;
    const [c, l] = cells.get(k0)!;
    let w = 1;
    while (i + w < keys.length && keys[i + w] === k0 + w) {
      const [c2, l2] = cells.get(k0 + w)!;
      if (c2 !== c || l2 !== l) break;
      w++;
    }
    let ci = css.indexOf(c);
    if (ci < 0) ci = css.push(c) - 1;
    out.push((k0 % 8192) - 4096, Math.floor(k0 / 8192) - 4096, w, l, ci);
    i += w;
  }
  const r: Runs = { n: out.length / 5, d: Int16Array.from(out), css };
  if (cache.size >= CAP) cache.delete(cache.keys().next().value as string);
  cache.set(key, r);
  return r;
}

/** Replays runs with their origin at (cx, cy) (rounded), each at min(run level, `cap`) (cap > 3 = no cap). */
export function drawRuns(g: G, r: Runs, cx: number, cy: number, cap = 3): void {
  const x0 = Math.round(cx);
  const y0 = Math.round(cy);
  let lastA = -1;
  let lastC = -1;
  const c = Math.max(0, Math.min(3, Math.round(cap)));
  for (let i = 0, p = 0; i < r.n; i++, p += 5) {
    const l = Math.min(r.d[p + 3]!, c);
    if (l <= 0) continue;
    const a = DITHER[l]!;
    if (a !== lastA) {
      g.globalAlpha = a;
      lastA = a;
    }
    const ci = r.d[p + 4]!;
    if (ci !== lastC) {
      g.fillStyle = r.css[ci]!;
      lastC = ci;
    }
    g.fillRect(x0 + r.d[p]!, y0 + r.d[p + 1]!, r.d[p + 2]!, 1);
  }
  g.globalAlpha = 1;
}
